import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, TablePagination, Typography, Popover, TextField, MenuItem, Button } from '@mui/material';
import BranchesListingTable from './branches-listing.table';
import { BranchType } from '../../../../interfaces/delivery-company/branches';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { PaginationContext } from '../../../../contexts/pagination-context';
import LebanonMap, { LocationsLink, LocationType } from '../../../common/lebanon-map/lebanon-map';
import CreateEntityDrawer from '../../../common/create-entity-drawer/create-entity-drawer';
import CreateBranchForm from '../create-branch-form/create-branch-form';
import Grid2 from '@mui/material/Unstable_Grid2';
import { IArea } from '../../../../interfaces/common/location';
import { useSnackBar } from '../../../../contexts/snackbar';

/** Minimal shape to feed into LebanonMap's locations prop */
type MinimalArea = {
  lat?: number | null;
  long?: number | null;
  district?: string;
};

type SampleItem = {
  area: {
    id: number;
    code: string;
    name_en: string;
    name_ar: string;
    lat: number | null;
    long: number | null;
    district: string;
  };
  localization: {
    partition_id: number | null;
    branch_id: number | null;
    method: string | null;
    distance_m: number | null;
    explanation?: string | null;
  };
};

type SampleResponse = {
  org_id: number;
  limit: number;
  eps_cover_m: number;
  tol_gap_m: number;
  count: number;
  items: SampleItem[];
};

function BranchesListing() {
  const [branchesList, setBranchesList] = useState<BranchType[]>([]);
  const [branchesCount, setBranchesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Links shown on the map - now loaded from backend
  const [links, setLinks] = useState<{ locationA: string; locationB: string; label?: string; id?: number }[]>([]);
  // Add link mode toggle
  const [addLinkMode, setAddLinkMode] = useState(false);
  // Popover state for creating a link from a clicked source branch
  const [linkSourceId, setLinkSourceId] = useState<string | null>(null);
  const [linkTargetId, setLinkTargetId] = useState<string | null>(null);
  const [estimatedTime, setEstimatedTime] = useState('');
  const [linkPopoverPos, setLinkPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);

  // samples
  const [samples, setSamples] = useState<SampleItem[] | null>(null);
  const [isLoadingSamples, setIsLoadingSamples] = useState(false);
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const { paginationConfig, setPaginationConfig } = useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: 'Name',
    searchValue: '',
    searchOperation: 'equals',
  });

  const getBranchConnections = async () => {
    try {
      const response = await axios.get<
        Array<{
          id: number;
          source: number;
          destination: number;
          estimated_travel_time: string;
        }>
      >(`${API_ENDPOINT}/api/v1/branch-connections/`, config);
      const connections = response.data.map(conn => ({
        locationA: String(conn.source),
        locationB: String(conn.destination),
        label: convertFromDjangoDuration(conn.estimated_travel_time),
        id: conn.id,
      }));
      setLinks(connections);
    } catch (error) {
      console.log('Error fetching branch connections:', error);
    }
  };

  const getData = async () => {
    let endpoint = `${API_ENDPOINT}/api/v1/branches/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}`;
    if (controller.searchColumn !== '' && controller.searchValue !== '') {
      endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${controller.searchValue}`;
    }

    try {
      setIsLoading(true);
      const response = await axios.get<{ count: number; results: BranchType[] }>(endpoint, config);
      setBranchesList(response.data.results);
      setBranchesCount(response.data.count);
      await getBranchConnections();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [controller]);

  // fetch samples once branches (and org) are known
  const fetchSamples = async () => {
    setIsLoadingSamples(true);
    try {
      const url = `${API_ENDPOINT}/api/v1/geo/sample-areas/`;
      const { data } = await axios.get<SampleResponse>(url, config);
      setSamples(data.items || []);
    } catch (e) {
      console.error('Failed to fetch samples', e);
      setSamples([]);
    } finally {
      setIsLoadingSamples(false);
    }
  };

  useEffect(() => {
    // only fetch when we actually have branches/org
    if (branchesList.length > 0) {
      fetchSamples();
    } else {
      setSamples(null);
    }
  }, [branchesList]);
  // Convert user-friendly time format (e.g., "45m", "2:30h", "1h 30m") to Django duration format (HH:MM:SS)
  const convertToDjangoDuration = (timeStr: string): string | null => {
    try {
      console.log('Converting time string:', timeStr);

      const cleaned = timeStr.trim().toLowerCase();
      let totalMinutes = 0;
      console.log('Converting time string:', cleaned);
      // Pattern: "2:30h" or "2:30" (hours:minutes)
      const hourMinPattern = /^(\d+):(\d+)h?$/;
      const hourMinMatch = cleaned.match(hourMinPattern);
      if (hourMinMatch) {
        const hours = parseInt(hourMinMatch[1]);
        const minutes = parseInt(hourMinMatch[2]);
        totalMinutes = hours * 60 + minutes;
      }
      // Pattern: "45m" (minutes only)
      else if (cleaned.endsWith('m')) {
        totalMinutes = parseInt(cleaned.replace('m', ''));
      }
      // Pattern: "2h" (hours only)
      else if (cleaned.endsWith('h')) {
        totalMinutes = parseInt(cleaned.replace('h', '')) * 60;
      }
      // Pattern: "1h 30m" (hours and minutes)
      else if (cleaned.includes('h') && cleaned.includes('m')) {
        const parts = cleaned.split(/\s+/);
        for (const part of parts) {
          if (part.endsWith('h')) {
            totalMinutes += parseInt(part.replace('h', '')) * 60;
          } else if (part.endsWith('m')) {
            totalMinutes += parseInt(part.replace('m', ''));
          }
        }
      }
      // Pattern: plain number (assume minutes)
      else if (/^\d+$/.test(cleaned)) {
        totalMinutes = parseInt(cleaned);
      }
      else {
        return null;
      }

      // Convert to HH:MM:SS format
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    } catch (error) {
      return null;
    }
  };

  // Convert Django duration format (HH:MM:SS or DD HH:MM:SS) to user-friendly format
  const convertFromDjangoDuration = (djangoDuration: string): string => {
    try {
      // Handle formats like "02:00:00" or "0 02:00:00"
      const parts = djangoDuration.trim().split(' ');
      const timeStr = parts.length > 1 ? parts[1] : parts[0];

      const [hoursStr, minutesStr] = timeStr.split(':');
      const hours = parseInt(hoursStr);
      const minutes = parseInt(minutesStr);

      if (hours === 0 && minutes === 0) {
        return '0m';
      } else if (hours === 0) {
        return `${minutes}m`;
      } else if (minutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${minutes}m`;
      }
    } catch (error) {
      return djangoDuration;
    }
  };

  const closeLinkPopover = () => {
    setLinkSourceId(null);
    setLinkTargetId(null);
    setEstimatedTime('');
    setLinkPopoverPos(null);
    setLinkLoading(false);
  };

  const commitLink = async () => {
    if (!linkSourceId || !linkTargetId || estimatedTime.trim() === '' || linkLoading) return;

    const exists = links.some(l => (l.locationA === linkSourceId && l.locationB === linkTargetId) || (l.locationA === linkTargetId && l.locationB === linkSourceId));
    if (exists) {
      showMessage('Connection already exists between these branches', 'warning');
      closeLinkPopover();
      return;
    }

    const djangoDuration = convertToDjangoDuration(estimatedTime);
    if (!djangoDuration) {
      showMessage('Invalid time format. Use formats like: 45m, 2h, 2:30h, or 1h 30m', 'error');
      return;
    }

    setLinkLoading(true);
    try {
      const response = await axios.post<{
        id: number;
        source: number;
        destination: number;
        estimated_travel_time: string;
      }>(`${API_ENDPOINT}/api/v1/branch-connections/`, {
        source: parseInt(linkSourceId),
        destination: parseInt(linkTargetId),
        estimated_travel_time: djangoDuration
      }, config);

      setLinks(prev => [...prev, {
        locationA: linkSourceId,
        locationB: linkTargetId,
        label: estimatedTime.trim(),
        id: response.data.id
      }]);

      showMessage('Branch connection created successfully', 'success');
      closeLinkPopover();
    } catch (error) {
      console.log('Error creating branch connection:', error);
      showMessage('Failed to create branch connection', 'error');
      setLinkLoading(false);
    }
  };


  const handleMapNodeClick = (id: string, e: React.MouseEvent) => {
    if (!addLinkMode) return;
    setLinkSourceId(id);
    setLinkTargetId(null);
    setEstimatedTime('');
    setLinkPopoverPos({ top: e.clientY, left: e.clientX });
  };

  const handleLinkUpdate = async (link: LocationsLink, newLabel: string) => {
    if (!link.id) return;

    // Convert user input to Django duration format
    const djangoDuration = convertToDjangoDuration(newLabel);
    if (!djangoDuration) {
      showMessage('Invalid time format. Use formats like: 45m, 2h, 2:30h, or 1h 30m', 'error');
      throw new Error('Invalid time format');
    }

    try {
      await axios.patch(`${API_ENDPOINT}/api/v1/branch-connections/${link.id}/`, {
        estimated_travel_time: djangoDuration
      }, config);

      setLinks(prev => prev.map(l =>
        l.id === link.id
          ? { ...l, label: newLabel }
          : l
      ));

      showMessage('Branch connection updated successfully', 'success');
    } catch (error) {
      console.log('Error updating branch connection:', error);
      showMessage('Failed to update branch connection', 'error');
      throw error; // Re-throw so the map component knows the update failed
    }
  };

  const handleLinkDelete = async (link: LocationsLink) => {
    if (!link.id) return;

    try {
      await axios.delete(`${API_ENDPOINT}/api/v1/branch-connections/${link.id}/`, config);

      setLinks(prev => prev.filter(l => l.id !== link.id));

      showMessage('Branch connection deleted successfully', 'success');
    } catch (error) {
      console.log('Error deleting branch connection:', error);
      showMessage('Failed to delete branch connection', 'error');
      throw error; // Re-throw so the map component knows the delete failed
    }
  };

  const unlinkedTargets = linkSourceId ? branchesList.filter(b => {
    const bid = String(b.id);
    if (bid === linkSourceId) return false;
    return !links.some(l => (l.locationA === linkSourceId && l.locationB === bid) || (l.locationB === linkSourceId && l.locationA === bid));
  }) : [];

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  // --- Compose map data: branch nodes + sampled area nodes + links ---
  const mapLocations = useMemo(() => {
    const branchNodes = branchesList.map((b) => ({
      id: String(b.id),
      label: b.name,
      area: b.area,
      width: 24,
      height: 24,
      type: LocationType.WAREHOUSE,
    }));

    const sampleNodes =
      (samples || [])
        .filter((s) => s.area.lat != null && s.area.long != null)
        .map((s) => ({
          id: `sample-${s.area.id}`,
          label: s.area.name_en ?? s.area.code ?? `Area ${s.area.id}`,
          area: {
            lat: s.area.lat,
            long: s.area.long,
            district: s.area.district || '',
          } as IArea,
          width: 16,
          height: 16,
          // If your map supports a different icon/type for samples, change this:
          type: LocationType.GENERIC,
        })) || [];

    return [...branchNodes, ...sampleNodes];
  }, [branchesList, samples]);

  const mapLinks = useMemo(() => {
    const links: { locationA: string | number; locationB: string | number; label?: string }[] = [];

    // draw a link from each sampled area to the chosen branch
    for (const s of samples || []) {
      if (s.localization.branch_id && s.area.lat != null && s.area.long != null) {
        const labelParts: string[] = [];
        if (s.localization.method) labelParts.push(String(s.localization.method));
        if (typeof s.localization.distance_m === 'number') {
          const m = Math.round(s.localization.distance_m);
          labelParts.push(`${m} m`);
        }
        links.push({
          locationA: `sample-${s.area.id}`,
          locationB: s.localization.branch_id,
        });
      }
    }

    return links;
  }, [samples]);

  const showSpinner = isLoading || !branchesList || branchesList.length < 2 || isLoadingSamples;

  return (
    <Grid2 container spacing={2}>
      <Grid2 xs={12} md={5}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <CreateEntityDrawer>
            <CreateBranchForm successCallback={() => getData()} />
          </CreateEntityDrawer>

          <TablePagination
            component="div"
            onPageChange={handlePageChange}
            page={controller.page}
            count={branchesCount}
            rowsPerPage={controller.rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
            labelRowsPerPage=""
            style={{ overflow: 'visible' }}
          />
        </Box>

        <BranchesListingTable
          branches={branchesList}
          isLoading={isLoading}
          updateBranch={(b) => {
            setBranchesList((prev) => prev.map((p) => (p.id == b.id ? b : p)))
            if (b.is_default) 
              setBranchesList((prev) => prev.map((p) => (p.id == b.id ? b : {...p, is_default: false})))
            }
          }
        />
      </Grid2>

      <Grid2 xs={12} md={7}>
        <Box margin="auto" width="100%" position={"relative"}>
               {(isLoading || !branchesList || branchesList.length < 2) ? <CircularProgress /> : (
                 <LebanonMap
                   locations={branchesList.map((b) => ({
                     area: b.area,
                     id: b.id,
                     label: b.name,
                     type: LocationType.WAREHOUSE,
                     width: 24,
                     height: 24,
                   }))}
                   links={links}
                   scale={14000}
                   zoom={7.3}
                   height={500}
                   showDistricts={false}
                   onNodeClick={handleMapNodeClick}
                   onLinkUpdate={handleLinkUpdate}
                   onLinkDelete={handleLinkDelete}
                   addLinkMode={addLinkMode}
                   onToggleAddLink={() => setAddLinkMode(m => !m)}
                   showPartitions={true}
                 />
               )}
            </Box>

      {/* Popover for adding a link */}
      <Popover
        open={Boolean(linkPopoverPos)}
        onClose={linkLoading ? undefined : closeLinkPopover}
        anchorReference="anchorPosition"
        anchorPosition={linkPopoverPos ? { top: linkPopoverPos.top, left: linkPopoverPos.left } : undefined}
      >
        <Box p={2} minWidth={260} display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle2">Create Link From Branch #{linkSourceId}</Typography>
          {linkSourceId && unlinkedTargets.length === 0 && (
            <Typography variant="body2">All branches already linked.</Typography>
          )}
          {unlinkedTargets.length > 0 && (
            <TextField
              select
              label="Target Branch"
              size="small"
              value={linkTargetId ?? ''}
              onChange={(e) => setLinkTargetId(e.target.value)}
              disabled={linkLoading}
              fullWidth
            >
              {unlinkedTargets.map(b => (
                <MenuItem key={b.id} value={String(b.id)}>#{b.id} {b.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Estimated Time"
            size="small"
            value={estimatedTime}
            onChange={(e) => setEstimatedTime(e.target.value)}
            placeholder="e.g. 45m or 2:30h"
            disabled={linkLoading}
            fullWidth
          />
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button size="small" onClick={closeLinkPopover} disabled={linkLoading}>Cancel</Button>
            <Button size="small" variant="contained" disabled={!linkSourceId || !linkTargetId || estimatedTime.trim() === '' || unlinkedTargets.length === 0 || linkLoading} onClick={commitLink}>
              {linkLoading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Popover>
      </Grid2>
    </Grid2>
  );
}

export default BranchesListing;
